import React, { useState } from "react";
import { Listbox } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/solid";

/** Default sponsor-role options per user row (dual portal). */
export const DEFAULT_EXISTING_SPONSOR_USER_ROLE_OPTIONS = [
  { id: "admin", title: "Sponsor Admin" },
  { id: "rfi-manager", title: "VQQ Manager" },
  { id: "audit-manager", title: "Audit & Assessment Manager" },
];

export const DEFAULT_EXISTING_VENDOR_USER_ROLE_OPTIONS = [
  { id: 'admin', title: 'Vendor Admin' },
  { id: 'rfi-manager-approver', title: 'VQQ Manager - Approver' },
  { id: 'rfi-manager-user', title: 'VQQ Manager - User' },
  { id: 'audit-manager', title: 'Audit & Assessment Manager' }
]

/**
 * Existing vendor users with radio selection and per-row sponsor role dropdown.
 * Parent should handle `onUserSelect` (e.g. react-hook-form setValue for name/email).
 */
const ExistingUserSelector = ({
  users,
  type = "vendor",
  defaultRoleId = "admin",
  onUserSelect,
  onRoleChange,
  title = type === "vendor"
    ? "Select Existing Vendor User"
    : "Select Existing Sponsor User",
  description = type === "vendor"
    ? "This organization has dual portal access. Select a vendor-side user to create their sponsor-side account."
    : "This organization has dual portal access. Select a sponsor-side user to create their vendor-side account.",
}) => {
  const roleOptions = type === "vendor" ? DEFAULT_EXISTING_SPONSOR_USER_ROLE_OPTIONS : DEFAULT_EXISTING_VENDOR_USER_ROLE_OPTIONS;
  const [selectedExistingUser, setSelectedExistingUser] = useState(null);
  const [existingUserRolesMap, setExistingUserRolesMap] = useState({});

  const handleExistingUserSelect = (user) => {
    if (selectedExistingUser?.id === user.id) {
      setSelectedExistingUser(null);
      onUserSelect?.(null);
    } else {
      setSelectedExistingUser(user);
      onUserSelect?.(user);
    }
  };

  const handleExistingUserRoleChange = (userId, roleId) => {
    setExistingUserRolesMap((prev) => ({ ...prev, [userId]: roleId }));
    if (selectedExistingUser?.id === userId) {
      onRoleChange?.(userId, roleId);
    }
  };

  if (!users?.length) {
    return null;
  }

  return (
    <div className="sm:col-span-full">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-900">{title}</label>
          <p className="mt-1 text-xs text-gray-500">{description}</p>
        </div>

        <div className="space-y-2">
          {users.map((user) => {
            const userRole = roleOptions.find(
              (r) => r.id === (existingUserRolesMap[user.id] || defaultRoleId)
            );
            const jobLine = user.job_title || user.title;

            return (
              <div
                key={user.id}
                className={`flex items-start bg-white rounded-md border p-3 transition-colors ${
                  selectedExistingUser?.id === user.id
                    ? "border-indigo-600 ring-1 ring-indigo-600 bg-indigo-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div
                  className="flex items-start flex-1 min-w-0 cursor-pointer"
                  onClick={() => handleExistingUserSelect(user)}
                >
                  <div className="flex h-6 shrink-0 items-center">
                    <input
                      type="radio"
                      name="existing-vendor-user"
                      checked={selectedExistingUser?.id === user.id}
                      onChange={() => handleExistingUserSelect(user)}
                      className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 cursor-pointer"
                    />
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900">
                      {user.first_name} {user?.last_name}
                    </span>
                    <span className="block text-sm text-gray-500">{user.email}</span>
                    {jobLine ? (
                      <span className="block text-xs text-gray-400">{jobLine}</span>
                    ) : null}
                  </div>
                </div>
                <div className="ml-4 flex-shrink-0" style={{ minWidth: "220px" }}>
                  <Listbox
                    value={userRole}
                    onChange={(role) => handleExistingUserRoleChange(user.id, role.id)}
                  >
                    <div className="relative">
                      <Listbox.Button
                        onClick={(e) => e.stopPropagation()}
                        className="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pr-2 pl-3 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <span className="col-start-1 row-start-1 truncate pr-6">
                          {userRole?.title || "Select role"}
                        </span>
                        <ChevronDownIcon
                          aria-hidden="true"
                          className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                        />
                      </Listbox.Button>
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                        {roleOptions.map((role) => (
                          <Listbox.Option
                            key={role.id}
                            value={role}
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                active ? "bg-indigo-600 text-white" : "text-gray-900"
                              }`
                            }
                          >
                            {({ active, selected }) => (
                              <>
                                <span
                                  className={`block truncate ${
                                    selected ? "font-semibold" : "font-normal"
                                  }`}
                                >
                                  {role.title}
                                </span>
                                {selected && (
                                  <span
                                    className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                      active ? "text-white" : "text-indigo-600"
                                    }`}
                                  >
                                    <CheckIcon className="size-5" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </div>
                  </Listbox>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExistingUserSelector;
